import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, betaAddSendAs } from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { SendAsModel } from '../../../../../../types';
import SharedDialog from '../../../../../shared/shared-dialog';
import { Controller, useForm } from 'react-hook-form';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../utils/message-constants';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface AddSendAsProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  sendAsAccounts: SendAsModel[];
  changeLogRoles: string[];
}

export default function AddSendAsDialog({
  open,
  handleClose,
  userEmail,
  sendAsAccounts,
  changeLogRoles,
}: AddSendAsProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  interface FormInput {
    sendAsEmail: string;
    displayName: string;
    replyToAddress: string;
    isDefault: boolean;
    treatAsAlias: boolean;
    isPrimary: boolean;
  }

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<FormInput>({
    defaultValues: {
      sendAsEmail: '',
      displayName: '',
      replyToAddress: '',
      isDefault: false,
      treatAsAlias: false,
      isPrimary: false,
    },
  });

  const onSubmit = (data: FormInput) => {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.AddGmailSendAs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: SendAsModel) =>
      betaAddSendAs(userEmail, data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Send As account'),
      });

      let newList = [];
      const currentDefault = sendAsAccounts.find(
        (account) => account.isDefault
      );
      if (
        currentDefault &&
        currentDefault.sendAsEmail !== data.sendAsEmail &&
        data.isDefault === true
      ) {
        currentDefault.isDefault = false;
        newList = sendAsAccounts.filter(
          (account) => account.sendAsEmail != currentDefault.sendAsEmail
        );
        newList.push(currentDefault, data);
      } else {
        newList.push(...sendAsAccounts, data);
      }

      queryClient.setQueryData([UserKeys.SendAsAccounts, userEmail], newList);
      setChangeLogMessage('');
      handleClose();
      reset();
      setAutoCompleteResponse('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating(
          'the send as account'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .create_send_as_email,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.sendAs,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  useEffect(() => {
    if (autoCompleteResponse.length > 0) {
      setValue('sendAsEmail', autoCompleteResponse, {
        shouldDirty: true,
      });
    } else {
      setValue('sendAsEmail', '', {
        shouldDirty: true,
      });
    }
  }, [autoCompleteResponse, setValue]);

  const isDefaultSwitch = watch('isDefault');
  const isAliasSwitch = watch('treatAsAlias');

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
        setAutoCompleteResponse('');
      }}
      title="Add Send As Account"
      dialogContent={
        <Stack gap={2} component="form">
          <Controller
            name={'sendAsEmail'}
            control={control}
            rules={{
              required: 'This is a required field.',
            }}
            render={() => (
              <Box>
                <SharedAutocomplete
                  autoFocus
                  sources={['users', 'groups']}
                  freeSolo
                  required
                  autocompleteValue={autoCompleteResponse}
                  setAutoCompleteValue={setAutoCompleteResponse}
                  errors={!!errors.sendAsEmail}
                  label="Search for a user or group"
                  helperText={
                    errors.sendAsEmail?.message
                      ? errors.sendAsEmail.message
                      : ''
                  }
                />
              </Box>
            )}
          />
          <Controller
            name={'displayName'}
            control={control}
            render={({ field }) => (
              <TextField
                id="displayName"
                label="Name"
                {...field}
                fullWidth
                autoComplete="off"
              />
            )}
          />
          <Controller
            name={'replyToAddress'}
            control={control}
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Must be a valid email address',
              },
            }}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  id="replyAddress"
                  label="Reply Address"
                  error={!!errors.replyToAddress}
                  helperText={
                    errors.replyToAddress?.message
                      ? errors.replyToAddress.message
                      : ''
                  }
                  autoComplete="off"
                />
              </Box>
            )}
          />
          <Stack gap={1}>
            <Controller
              name={'isDefault'}
              control={control}
              render={({ field }) => {
                return (
                  <FormControlLabel
                    sx={{ width: 'fit-content' }}
                    control={
                      <Checkbox
                        onChange={() => {
                          setValue('isDefault', !isDefaultSwitch, {
                            shouldDirty: true,
                          });
                        }}
                        checked={field.value === true}
                      />
                    }
                    label={<Typography>Set this account as default</Typography>}
                  />
                );
              }}
            />
            <Controller
              name={'treatAsAlias'}
              control={control}
              render={({ field }) => {
                return (
                  <FormControlLabel
                    sx={{ width: 'fit-content' }}
                    control={
                      <Checkbox
                        onChange={() => {
                          setValue('treatAsAlias', !isAliasSwitch, {
                            shouldDirty: true,
                          });
                        }}
                        checked={field.value === true}
                      />
                    }
                    label={<Typography>Treat as an alias</Typography>}
                  />
                );
              }}
            />
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() =>
              mutate({
                sendAsEmail: getValues('sendAsEmail'),
                displayName: getValues('displayName'),
                replyToAddress: getValues('replyToAddress'),
                isDefault: getValues('isDefault'),
                treatAsAlias: getValues('treatAsAlias'),
                isPrimary: getValues('isPrimary'),
              })
            }
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled: isPending,
        title: 'Add Account',
      }}
    />
  );
}
